import React from 'react';

/*
 * The twelve preset badges (canvas "Calendar Redesign" › Promotions, PRO 02):
 * three shapes, each with a symbol and a colour, stored by key on the
 * promotion. A copy of the admin's PromotionBadge (mpgadmin), so a client
 * sees the badge the owner picked. Anything unknown draws the first.
 */

type Shape = 'tag' | 'burst' | 'ticket';
type Glyph = 'dollar' | 'percent' | 'star' | 'gift' | 'heart' | 'paw';

export interface BadgeSpec {
  key: string;
  shape: Shape;
  glyph: Glyph;
  color: string;
  /** The card's soft panel behind the badge. */
  tint: string;
}

const B = (shape: Shape, glyph: Glyph, name: string, color: string, tint: string): BadgeSpec => ({
  key: `${shape}-${glyph}-${name}`,
  shape,
  glyph,
  color,
  tint,
});

export const BADGES: BadgeSpec[] = [
  B('tag', 'dollar', 'green', '#12A150', '#E9F9EE'),
  B('burst', 'percent', 'orange', '#F08C00', '#FFF3E0'),
  B('ticket', 'dollar', 'blue', '#0066FF', '#E5EFFF'),
  B('burst', 'dollar', 'red', '#E5384F', '#FDECEE'),
  B('burst', 'percent', 'teal', '#0E9AA0', '#E3F6F6'),
  B('burst', 'star', 'purple', '#7B4EE0', '#F1ECFD'),
  B('tag', 'percent', 'orange', '#F08C00', '#FFF3E0'),
  B('ticket', 'percent', 'green', '#12A150', '#E9F9EE'),
  B('ticket', 'gift', 'red', '#E5384F', '#FDECEE'),
  B('burst', 'heart', 'red', '#E5384F', '#FDECEE'),
  B('tag', 'paw', 'purple', '#7B4EE0', '#F1ECFD'),
  B('ticket', 'paw', 'orange', '#F08C00', '#FFF3E0'),
];

export const badgeFor = (key?: string | null): BadgeSpec =>
  BADGES.find(b => b.key === key) ?? BADGES[0];

const BURST =
  'M45.0,24.0 L40.9,28.5 L42.2,34.5 L36.4,36.4 L34.5,42.2 L28.5,40.9 L24.0,45.0 L19.5,40.9 L13.5,42.2 L11.6,36.4 L5.8,34.5 L7.1,28.5 L3.0,24.0 L7.1,19.5 L5.8,13.5 L11.6,11.6 L13.5,5.8 L19.5,7.1 L24.0,3.0 L28.5,7.1 L34.5,5.8 L36.4,11.6 L42.2,13.5 L40.9,19.5Z';
const TICKET =
  'M6 12a3 3 0 0 1 3-3h30a3 3 0 0 1 3 3v6a6 6 0 0 0 0 12v6a3 3 0 0 1-3 3H9a3 3 0 0 1-3-3v-6a6 6 0 0 0 0-12Z';
const TAG = 'M3 15a3 3 0 0 1 3-3h25l14 12-14 12H6a3 3 0 0 1-3-3Z';

const Paw = () => (
  <g fill="#FFFFFF">
    <circle cx="19" cy="19.5" r="2.6" />
    <circle cx="29" cy="19.5" r="2.6" />
    <circle cx="15.5" cy="25" r="2.4" />
    <circle cx="32.5" cy="25" r="2.4" />
    <path d="M24 24c-4 0-6.5 4-6.5 6.3 0 1.6 1.3 2.4 2.8 2.4h7.4c1.5 0 2.8-.8 2.8-2.4C30.5 28 28 24 24 24Z" />
  </g>
);

const Text: React.FC<{ x: number; y: number; size: number; children: string }> = ({
  x,
  y,
  size,
  children,
}) => (
  <text
    x={x}
    y={y}
    textAnchor="middle"
    fontFamily="Inter,Arial,sans-serif"
    fontSize={size}
    fontWeight={800}
    fill="#FFFFFF"
  >
    {children}
  </text>
);

/** The symbol, centred on a 48×48 badge. */
const GlyphMark: React.FC<{ glyph: Glyph }> = ({ glyph }) => {
  switch (glyph) {
    case 'dollar':
      return (
        <Text x={24} y={31} size={20}>
          $
        </Text>
      );
    case 'percent':
      return (
        <Text x={24} y={31} size={19}>
          %
        </Text>
      );
    case 'star':
      return (
        <path
          d="M24 14.5l2.9 5.9 6.5.9-4.7 4.6 1.1 6.4-5.8-3.1-5.8 3.1 1.1-6.4-4.7-4.6 6.5-.9Z"
          fill="#FFFFFF"
        />
      );
    case 'heart':
      return (
        <path
          d="M24 33s-8.5-5-8.5-11.2A4.6 4.6 0 0 1 24 19.3a4.6 4.6 0 0 1 8.5 2.5C32.5 28 24 33 24 33Z"
          fill="#FFFFFF"
        />
      );
    case 'gift':
      return (
        <g fill="none" stroke="#FFFFFF" strokeWidth="2.4" strokeLinejoin="round">
          <path d="M15.5 22h17v10.5h-17zM14 18h20v4H14zM24 18v14.5" />
          <path d="M24 18c-2-3.8-6.5-4.6-6.8-2.2S21 18 24 18Zm0 0c2-3.8 6.5-4.6 6.8-2.2S27 18 24 18Z" />
        </g>
      );
    case 'paw':
      return <Paw />;
  }
};

/** A promotion that doesn't fit this booking draws its badge grey (SF 07). */
const MUTED = '#A9AEB8';

export const PromotionBadge: React.FC<{ icon?: string | null; size?: number; label?: string; muted?: boolean }> = ({
  icon,
  size = 34,
  label,
  muted,
}) => {
  const spec = badgeFor(icon);
  const b = muted ? { ...spec, color: MUTED } : spec;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      style={{ flexShrink: 0, display: 'block' }}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      {b.shape === 'burst' && (
        <>
          <path
            d={BURST}
            fill={b.color}
            stroke={b.color}
            strokeWidth="2.5"
            strokeLinejoin="round"
          />
          <GlyphMark glyph={b.glyph} />
        </>
      )}
      {b.shape === 'ticket' && (
        <>
          <path d={TICKET} fill={b.color} />
          <GlyphMark glyph={b.glyph} />
        </>
      )}
      {b.shape === 'tag' && (
        <g transform="rotate(-32 24 24)">
          <path
            d={TAG}
            fill={b.color}
            stroke={b.color}
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <circle cx="36.5" cy="24" r="2.6" fill="#FFFFFF" />
          <g transform="translate(-5 0)">
            {b.glyph === 'paw' ? (
              <g transform="translate(24 24) scale(.66) translate(-24 -24)">
                <Paw />
              </g>
            ) : (
              <GlyphMark glyph={b.glyph} />
            )}
          </g>
        </g>
      )}
    </svg>
  );
};

export default PromotionBadge;
